import { ReactNode, useEffect, useRef, useState } from 'react';

export interface DropdownItem {
  key: string;
  label?: string;
  danger?: boolean;
  divider?: boolean;
}

interface DropdownProps {
  items: DropdownItem[];
  onSelect?: (key: string) => void;
  children: ReactNode;
  align?: 'left' | 'right';
}

/** Lightweight click-triggered dropdown menu — replaces antd Dropdown. */
export default function Dropdown({ items, onSelect, children, align = 'right' }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center">
        {children}
      </button>

      {open && (
        <div
          className={[
            'absolute top-full z-30 mt-2 min-w-[180px] overflow-hidden rounded-lg border border-line/40 bg-white py-1 shadow-card',
            align === 'right' ? 'right-0' : 'left-0',
          ].join(' ')}
        >
          {items.map((item) =>
            item.divider ? (
              <div key={item.key} className="my-1 h-px bg-line/40" />
            ) : (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  onSelect?.(item.key);
                  setOpen(false);
                }}
                className={[
                  'block w-full px-4 py-2 text-left text-sm transition-colors',
                  item.danger
                    ? 'text-red-500 hover:bg-red-50'
                    : 'text-ink hover:bg-brand-shade hover:text-brand',
                ].join(' ')}
              >
                {item.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}
